import { Component, input, effect, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import {
  PetitionResponse,
  DriverResponse,
  DriverAssignment,
  DriverAssignmentResponse,
} from 'src/app/shared/interfaces/holidays.interface';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

@Component({
  selector: 'app-petitions',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    QeqPipe,
  ],
  templateUrl: './petitions.component.html',
  styleUrls: ['./petitions.component.css', '../../../styles/layout.css'],
})
export class PetitionsComponent implements OnInit {
  displayedColumns: string[] = [
    'employee_id',
    'name',
    'company',
    'age',
    'status',
    'vacation_days',
    'actions',
  ];

  // input signals
  readonly onlyPetitions = input<boolean>();
  readonly operativeId = input<number>();
  readonly exerciseId = input<number>();

  // data from API - union type to support both petitions and drivers
  dataSource: (PetitionResponse | DriverResponse | DriverAssignment)[] = [];

  // track expanded rows (only used when onlyPetitions() === false)
  readonly expanded = signal<Set<number>>(new Set());

  constructor(
    private router: Router,
    private holidaysService: HolidaysService
  ) {}

  ngOnInit(): void {
    const opId = this.operativeId();
    const exId = this.exerciseId();
    if (opId && exId) {
      this.loadData(opId, exId);
    }
  }

  loadData(operativeId: number, exerciseId: number): void {
    if (this.onlyPetitions()) {
      // Load petitions (employees with pending requests)
      this.holidaysService.getOperativeRequests(operativeId, false).subscribe({
        next: (data) => {
          // Filter only employees with pending requests
          this.dataSource = data.filter(
            (item) => item.requests && item.requests.length > 0
          );
          console.log('Petitions data:', this.dataSource);
        },
        error: (error) => {
          console.error('Error fetching petitions:', error);
        },
      });
    } else {
      // Load driver assignments using the new endpoint
      this.holidaysService
        .getDriverAssignments({
          operativa: operativeId,
          exercise: exerciseId,
        })
        .subscribe({
          next: (response: DriverAssignmentResponse) => {
            this.dataSource = response.results;
            console.log('Driver assignments data:', this.dataSource);
          },
          error: (error) => {
            console.error('Error fetching driver assignments:', error);
          },
        });
    }
  }

  expandedHas(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): boolean {
    if (this.isDriverAssignment(row)) {
      return this.expanded().has(row.driver);
    }
    return this.expanded().has(row.employee.id);
  }

  toggleRow(row: PetitionResponse | DriverResponse | DriverAssignment): void {
    if (this.onlyPetitions()) return; // no toggle in petitions-only mode
    const id = this.isDriverAssignment(row) ? row.driver : row.employee.id;
    this.expanded.update((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Type guard to check if a row is a DriverAssignment
  isDriverAssignment(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is DriverAssignment {
    return 'driver' in row && typeof row.driver === 'number';
  }

  // Type guard to check if a row is a PetitionResponse
  isPetitionResponse(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is PetitionResponse {
    return 'requests' in row;
  }

  // Type guard to check if a row is a DriverResponse
  isDriverResponse(
    row: PetitionResponse | DriverResponse | DriverAssignment
  ): row is DriverResponse {
    return 'actions' in row;
  }

  onApproveRequest(requestId: number): void {
    console.log('Approve request:', requestId);
    // TODO: Implement API call to approve request
  }

  onDenyRequest(requestId: number): void {
    console.log('Deny request:', requestId);
    // TODO: Implement API call to deny request
  }

  goToProfile(employeeId: number): void {
    // Use the actual exercise and operative IDs from the inputs
    const exerciseId = this.exerciseId() || 1;
    const operationalId = this.operativeId() || 1;
    this.router.navigate([
      '/home',
      'vacations',
      'drivers',
      'exercise',
      exerciseId,
      'operational',
      operationalId,
      'driver',
      employeeId,
    ]);
  }
}
